import { AlertToastType, ToastController } from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router';
import type { DrawerMode } from './ConnectionDrawer';
import { type Connection, getConnection } from './connections';

interface ConnectionDrawerPaths {
    create?: string;
    edit?: string;
}

export const useConnectionDrawer = (
    basePath: string,
    connections: Connection[],
    paths: ConnectionDrawerPaths = {},
) => {
    const createPath = paths.create ?? 'new';
    const editPath = paths.edit ?? '';

    const navigate = useNavigate();
    const location = useLocation();
    const newMatch = useMatch(`/${basePath}/new`);
    const connectorMatch = useMatch(`/${basePath}/${createPath}/:id`);
    const connectorViewMatch = useMatch(`/${basePath}/connector/:id`);
    const connectionMatch = useMatch(
        editPath ? `/${basePath}/${editPath}/:id` : `/${basePath}/:id`,
    );

    const urlConnectorId = connectorMatch?.params.id ?? null;
    const urlConnectorViewId = connectorViewMatch?.params.id ?? null;
    const urlConnectionId = connectionMatch?.params.id ?? null;
    const isNewUrl = !!newMatch;
    const fromList = new URLSearchParams(location.search).get('from') === 'list';

    const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null);

    useEffect(() => {
        if (urlConnectorId) {
            setDrawerMode({ type: 'create-connection', connectorId: urlConnectorId, fromList });
        } else if (isNewUrl) {
            setDrawerMode({ type: 'create-connection' });
        } else if (urlConnectorViewId) {
            setDrawerMode({ type: 'view-connector', connectorId: urlConnectorViewId });
        } else if (urlConnectionId) {
            (async () => {
                try {
                    const connection =
                        connections.find((c) => c.appId === urlConnectionId) ??
                        (await getConnection(urlConnectionId));
                    setDrawerMode(
                        connection
                            ? {
                                  type: 'view-connection',
                                  connectorId: connection.connectorId,
                                  connectionId: connection.appId,
                              }
                            : null,
                    );
                } catch (err) {
                    ToastController.show({
                        description: `Error loading config with the ID "${urlConnectionId}": ${err}.`,
                        type: AlertToastType.ERROR,
                    });
                }
            })();
        } else {
            setDrawerMode(null);
        }
    }, [urlConnectorId, isNewUrl, urlConnectorViewId, urlConnectionId, connections, fromList]);

    const openCreate = () => navigate(`/${basePath}/new`);
    const openConnection = (appId: string) =>
        navigate(editPath ? `/${basePath}/${editPath}/${appId}` : `/${basePath}/${appId}`);
    const onConnectorSelect = (connectorId: string, fromList = false) =>
        navigate(`/${basePath}/${createPath}/${connectorId}${fromList ? '?from=list' : ''}`);
    const openConnectorView = (connectorId: string) =>
        navigate(`/${basePath}/connector/${connectorId}`);
    const navigateToBase = () => navigate(`/${basePath}`);

    return {
        drawerMode,
        setDrawerMode,
        openCreate,
        openConnection,
        onConnectorSelect,
        openConnectorView,
        navigateToBase,
    };
};
